import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import RefreshIcon from '@material-ui/icons/Refresh'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import EditVendorDetailsModal from '../Vendors/EditVendorDetailsModal'
import SubcontractorManagementPanel from '../Vendors/SubcontractorManagementPanel'
import NewSupplierModal from '../Proposals/NewSupplierModal'
import SubcontractorMessagingPanel from './SubcontractorMessagingPanel'

// ─── Theme-aligned palette tokens ───────────────────────────────────────────
const TEAL = 'rgb(119,156,164)'
const TEAL_LIGHT = 'rgb(172,202,205)'
const TEAL_DARK = 'rgb(81,110,118)'
const TEAL_BG = 'rgba(119,156,164,0.06)'
const TEAL_BG_HOVER = 'rgba(119,156,164,0.12)'
const TEAL_BG_SELECTED = 'rgba(119,156,164,0.14)'
const WARM = '#9e7747'
const WARM_BG = 'rgba(158,119,71,0.08)'
const TEXT_PRIMARY = '#263238'
const TEXT_SECONDARY = '#607d8b'
const TEXT_MUTED = '#90a4ae'
const BORDER = '#e0e8eb'
const BORDER_LIGHT = '#ecf2f4'
const SURFACE = '#fff'
const SURFACE_TINTED = 'rgba(119,156,164,0.03)'

const STATUS_COLORS = {
  ready: { bg: '#e8f5e9', text: '#2e7d32', dot: '#4caf50' },
  pending: { bg: 'rgba(119,156,164,0.12)', text: TEAL_DARK, dot: TEAL },
  setup: { bg: '#fff3e0', text: '#e65100', dot: '#ff9800' }
}

const useStyles = makeStyles((theme) => ({
  // ─── Page layout ────────────────────────────────────────────────
  page: {
    height: 'calc(100vh - 90px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2, 2.5)
  },

  // ─── Compact header bar ─────────────────────────────────────────
  headerBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2.5),
    borderRadius: 14,
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    minHeight: 56
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5)
  },
  headerTitle: {
    fontWeight: 800,
    fontSize: '1.15rem',
    color: TEXT_PRIMARY,
    letterSpacing: '-0.01em'
  },
  headerMetrics: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    flexWrap: 'wrap'
  },
  headerMetric: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 5
  },
  headerMetricValue: {
    fontWeight: 800,
    fontSize: '1.1rem',
    color: TEXT_PRIMARY
  },
  headerMetricLabel: {
    fontSize: '0.78rem',
    color: TEXT_MUTED,
    fontWeight: 500,
    whiteSpace: 'nowrap'
  },
  headerDivider: {
    width: 1,
    height: 28,
    background: BORDER,
    flexShrink: 0
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },

  // ─── Main content area ──────────────────────────────────────────
  mainContent: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(2),
    overflow: 'hidden',
    minHeight: 0
  },

  // ─── Vendor sidebar ─────────────────────────────────────────────
  sidebar: {
    width: 340,
    minWidth: 340,
    display: 'flex',
    flexDirection: 'column',
    background: SURFACE,
    border: `1px solid ${BORDER}`,
    borderRadius: 14,
    overflow: 'hidden'
  },
  sidebarHeader: {
    padding: theme.spacing(2, 2, 1.5),
    borderBottom: `1px solid ${BORDER_LIGHT}`
  },
  sidebarTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.25)
  },
  sidebarTitle: {
    fontWeight: 700,
    fontSize: '0.92rem',
    color: TEXT_PRIMARY,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
  },
  vendorCount: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    fontWeight: 600,
    background: TEAL_BG,
    borderRadius: 10,
    padding: '2px 8px'
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 10,
      fontSize: '0.875rem',
      backgroundColor: SURFACE_TINTED,
      '& fieldset': {
        borderColor: BORDER_LIGHT
      },
      '&:hover fieldset': {
        borderColor: TEAL_LIGHT
      },
      '&.Mui-focused fieldset': {
        borderColor: TEAL,
        borderWidth: 1
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.85rem'
    }
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1)
  },
  filterSwitch: {
    '& .MuiSwitch-colorPrimary.Mui-checked': {
      color: TEAL
    },
    '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: TEAL
    }
  },
  filterLabel: {
    fontSize: '0.78rem',
    color: TEXT_SECONDARY
  },

  // ─── Vendor list ────────────────────────────────────────────────
  vendorList: {
    flex: 1,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 5
    },
    '&::-webkit-scrollbar-thumb': {
      background: BORDER,
      borderRadius: 3
    }
  },
  vendorItem: {
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    transition: 'background-color 0.15s ease',
    outline: 'none',
    '&:hover': {
      backgroundColor: TEAL_BG_HOVER
    }
  },
  vendorItemSelected: {
    backgroundColor: TEAL_BG_SELECTED,
    borderLeft: `3px solid ${TEAL}`,
    '&:hover': {
      backgroundColor: TEAL_BG_SELECTED
    }
  },
  vendorName: {
    fontWeight: 700,
    fontSize: '0.88rem',
    color: TEXT_PRIMARY,
    lineHeight: 1.3
  },
  vendorMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: 4
  },
  vendorMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    fontWeight: 500
  },
  statusDot: {
    fontSize: 8
  },
  statusBadge: {
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '1px 7px',
    borderRadius: 6,
    letterSpacing: '0.02em'
  },
  emptyVendorList: {
    padding: theme.spacing(4, 2),
    textAlign: 'center'
  },

  // ─── Detail panel ───────────────────────────────────────────────
  detailPanel: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  },
  detailScroll: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing(0.5),
    '&::-webkit-scrollbar': {
      width: 5
    },
    '&::-webkit-scrollbar-thumb': {
      background: BORDER,
      borderRadius: 3
    }
  },

  // ─── Sticky vendor header ───────────────────────────────────────
  vendorHeaderSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: SURFACE,
    borderBottom: `1px solid ${BORDER}`,
    padding: theme.spacing(1.5, 3),
    marginLeft: theme.spacing(-0.5),
    marginRight: theme.spacing(-0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    minHeight: 48
  },
  stickyName: {
    fontWeight: 800,
    fontSize: '1rem',
    color: TEXT_PRIMARY,
    marginRight: theme.spacing(1)
  },
  stickyRight: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },

  // ─── Vendor header card ─────────────────────────────────────────
  vendorHeader: {
    borderRadius: 14,
    border: `1px solid ${BORDER}`,
    background: SURFACE,
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(2)
  },
  vendorHeaderTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5)
  },
  vendorDetailName: {
    fontWeight: 800,
    fontSize: '1.35rem',
    color: TEXT_PRIMARY,
    letterSpacing: '-0.01em',
    lineHeight: 1.2
  },
  vendorContact: {
    fontSize: '0.82rem',
    color: TEXT_SECONDARY,
    marginTop: 2
  },
  editButton: {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.82rem',
    padding: theme.spacing(0.75, 2),
    borderColor: TEAL,
    color: TEAL_DARK,
    '&:hover': {
      borderColor: TEAL_DARK,
      backgroundColor: TEAL_BG
    }
  },
  vendorStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${BORDER_LIGHT}`
  },
  vendorStatusChip: {
    height: 26,
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 7
  },
  vendorHint: {
    fontSize: '0.82rem',
    color: TEXT_SECONDARY,
    marginTop: theme.spacing(1.5),
    lineHeight: 1.5
  },

  // ─── Metric pills in header ─────────────────────────────────────
  vendorMetrics: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginTop: theme.spacing(2)
  },
  vendorMetricPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 12px',
    borderRadius: 8,
    border: `1px solid ${BORDER}`,
    background: SURFACE_TINTED
  },
  vendorMetricPillValue: {
    fontWeight: 800,
    fontSize: '0.95rem',
    color: TEXT_PRIMARY
  },
  vendorMetricPillLabel: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    fontWeight: 500
  },

  // ─── Checklist & actions grid ───────────────────────────────────
  twoColGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  cardLabel: {
    fontWeight: 700,
    fontSize: '0.82rem',
    color: TEXT_SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: theme.spacing(1.5)
  },
  checklistRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  checkIcon: {
    fontSize: 18,
    marginTop: 2,
    flexShrink: 0
  },
  checkIconDone: {
    color: '#4caf50'
  },
  checkIconPending: {
    color: BORDER
  },
  checklistText: {
    fontSize: '0.84rem',
    color: TEXT_PRIMARY,
    fontWeight: 600,
    lineHeight: 1.35
  },
  checklistDetail: {
    fontSize: '0.78rem',
    color: TEXT_MUTED,
    marginTop: 2,
    lineHeight: 1.4
  },
  actionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  actionArrow: {
    fontSize: 16,
    color: TEAL,
    marginTop: 3,
    flexShrink: 0
  },
  actionTitle: {
    fontSize: '0.84rem',
    color: TEXT_PRIMARY,
    fontWeight: 600,
    lineHeight: 1.35
  },
  actionDetail: {
    fontSize: '0.78rem',
    color: TEXT_MUTED,
    marginTop: 2,
    lineHeight: 1.4
  },

  // ─── Collapsible section ────────────────────────────────────────
  collapsibleSection: {
    borderRadius: 14,
    border: `1px solid ${BORDER}`,
    background: SURFACE,
    marginBottom: theme.spacing(2),
    overflow: 'hidden'
  },
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2.5),
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: TEAL_BG
    }
  },
  collapsibleHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  collapsibleTitle: {
    fontWeight: 700,
    fontSize: '0.88rem',
    color: TEXT_PRIMARY
  },
  collapsibleChevron: {
    fontSize: 20,
    color: TEXT_MUTED,
    transition: 'transform 0.2s ease'
  },
  collapsibleChevronOpen: {
    transform: 'rotate(180deg)'
  },
  collapsibleBody: {
    overflow: 'hidden',
    transition: 'max-height 0.25s ease, opacity 0.2s ease'
  },
  collapsibleBodyOpen: {
    maxHeight: 2000,
    opacity: 1
  },
  collapsibleBodyClosed: {
    maxHeight: 0,
    opacity: 0
  },
  collapsibleContent: {
    padding: theme.spacing(0, 2.5, 2.5)
  },

  // ─── Empty state ────────────────────────────────────────────────
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 14,
    border: `1px dashed ${BORDER}`,
    background: SURFACE_TINTED,
    padding: theme.spacing(6)
  },
  emptyTitle: {
    fontWeight: 700,
    fontSize: '1.05rem',
    color: TEXT_PRIMARY,
    marginBottom: theme.spacing(1)
  },
  emptyDesc: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    textAlign: 'center',
    maxWidth: 380,
    lineHeight: 1.55
  },

}))

// ─── Pure helpers (unchanged logic) ───────────────────────────────────────────

const getEmployeeAccessStatus = (employee) => {
  if (employee?.subcontractorAccessStatus) return employee.subcontractorAccessStatus
  return employee?.activeEmployee === false ? 'offboarded' : 'active'
}

const countVendorUsers = (employees, vendorId) => {
  const counts = { total: 0, active: 0, invited: 0, offboarded: 0, admins: 0, portalEnabled: 0 }
  const vid = String(vendorId)
  for (let i = 0; i < employees.length; i++) {
    const emp = employees[i]
    if (String(emp.associatedSub || '') !== vid) continue
    const status = getEmployeeAccessStatus(emp)
    counts.total++
    if (status === 'active') counts.active++
    else if (status === 'invited') counts.invited++
    else if (status === 'offboarded') counts.offboarded++
    if (emp.subcontractorRole === 'admin' && status !== 'offboarded') counts.admins++
    if (emp.canUseSubcontractorPortal) counts.portalEnabled++
  }
  return counts
}

const countVendorCrews = (crews, vendorId) => (
  crews.filter(item => String(item.subcontractorVendor || '') === String(vendorId)).length
)

const getVendorLaunchState = (vendor, userCounts, crewCount) => {
  const onboardingStatus = vendor?.onboardingStatus || 'draft'
  const checklist = [
    {
      label: 'Vendor is marked as a subcontractor',
      complete: !!vendor?.isSubcontractor,
      detail: vendor?.isSubcontractor ? 'This vendor can be managed from the subcontractor admin flow.' : 'Open vendor settings and enable subcontractor access.'
    },
    {
      label: 'Mobile access is enabled',
      complete: !!vendor?.mobileAccessEnabled,
      detail: vendor?.mobileAccessEnabled ? 'Users can access the subcontractor mobile experience.' : 'Turn on mobile access before launch.'
    },
    {
      label: 'Onboarding state is active',
      complete: onboardingStatus === 'active',
      detail: `Current onboarding status: ${onboardingStatus}.`
    },
    {
      label: 'An admin user is assigned',
      complete: userCounts.admins > 0,
      detail: userCounts.admins > 0 ? `${userCounts.admins} admin user linked.` : 'Add at least one subcontractor admin user.'
    },
    {
      label: 'At least one crew is linked',
      complete: crewCount > 0,
      detail: crewCount > 0 ? `${crewCount} linked crew${crewCount === 1 ? '' : 's'}.` : 'Create or assign a crew for this vendor.'
    }
  ]

  const actionItems = []

  if (!vendor?.isSubcontractor) {
    actionItems.push({
      title: 'Enable subcontractor mode',
      detail: 'Convert this vendor into a subcontractor org before inviting users or linking crews.'
    })
  }

  if (vendor?.isSubcontractor && !vendor?.mobileAccessEnabled) {
    actionItems.push({
      title: 'Turn on mobile access',
      detail: 'Portal users will not have the intended subcontractor experience until mobile access is enabled.'
    })
  }

  if (vendor?.isSubcontractor && onboardingStatus !== 'active') {
    actionItems.push({
      title: 'Move onboarding to active',
      detail: 'Keep draft while configuring, then mark active when the vendor is ready to launch.'
    })
  }

  if (vendor?.isSubcontractor && userCounts.admins === 0) {
    actionItems.push({
      title: 'Add an admin user',
      detail: 'Every subcontractor org should have an admin who can receive invites and manage access.'
    })
  }

  if (vendor?.isSubcontractor && userCounts.total === 0) {
    actionItems.push({
      title: 'Create the first portal user',
      detail: 'Use the vendor management panel below to add a subcontractor user and launch the existing invite flow.'
    })
  } else if (userCounts.invited > 0) {
    actionItems.push({
      title: 'Follow up on pending invites',
      detail: `${userCounts.invited} invite${userCounts.invited === 1 ? ' is' : 's are'} still waiting for completion.`
    })
  }

  if (vendor?.isSubcontractor && crewCount === 0) {
    actionItems.push({
      title: 'Link a crew',
      detail: 'Assign at least one subcontractor crew so the vendor setup feels complete and operational.'
    })
  }

  let stageKey = 'ready'
  if (!vendor?.isSubcontractor || !vendor?.mobileAccessEnabled || onboardingStatus !== 'active' || userCounts.admins === 0 || crewCount === 0) {
    stageKey = 'setup'
  } else if (userCounts.invited > 0 || userCounts.active === 0) {
    stageKey = 'pending'
  }

  return {
    crewCount,
    stageKey,
    stageLabel: stageKey === 'ready' ? 'Launch Ready' : stageKey === 'pending' ? 'Invites Pending' : 'Needs Setup',
    nextAction: actionItems[0]?.title || 'Review linked users and crews',
    checklist,
    actionItems: actionItems.length ? actionItems : [{
      title: 'Vendor is ready to launch',
      detail: 'Settings, access, and crew alignment are in place. Keep an eye on new invites and crew changes.'
    }]
  }
}

const getVendorSortPriority = (stageKey) => {
  if (stageKey === 'setup') return 0
  if (stageKey === 'pending') return 1
  return 2
}

// ─── Component ────────────────────────────────────────────────────────────────

const SubcontractorAdminPage = ({ url, notification }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [vendors, setVendors] = useState([])
  const [employees, setEmployees] = useState([])
  const [crews, setCrews] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const [showAllVendors, setShowAllVendors] = useState(false)
  const [selectedVendorId, setSelectedVendorId] = useState(null)
  const [editingVendor, setEditingVendor] = useState(null)
  const [showCreateVendorModal, setShowCreateVendorModal] = useState(false)
  const [collapsedSections, setCollapsedSections] = useState({})
  const vendorListRef = useRef(null)
  const vendorItemRefs = useRef({})

  const toggleSection = useCallback((key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [vendorsResponse, employeesResponse] = await Promise.all([
        axios.get(`${url}/api/company/getVendors`),
        axios.get(`${url}/api/employees/listEmployees?entity=All&includeInactive=true`)
      ])

      setVendors(vendorsResponse.data || [])
      setEmployees(employeesResponse.data?.employees || [])
      setCrews(employeesResponse.data?.crews || [])
    } catch (error) {
      console.error('Failed to load subcontractor admin data', error)
      notification?.({
        type: 'danger',
        title: 'Load Failed',
        message: 'Unable to load subcontractor vendors right now.'
      })
    } finally {
      setLoading(false)
    }
  }, [notification, url])

  useEffect(() => {
    loadData()
  }, [loadData])

  const subcontractorCount = useMemo(() => (vendors || []).filter(v => v.isSubcontractor).length, [vendors])

  const visibleVendors = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase()
    let next = vendors || []

    if (!showAllVendors) {
      next = next.filter(item => item.isSubcontractor)
    }

    if (normalizedSearch) {
      next = next.filter(item => {
        const haystack = [
          item.name,
          item.phoneNumber,
          item.address,
          item.vendorType,
          item.city,
          item.state
        ].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(normalizedSearch)
      })
    }

    return next
  }, [searchValue, showAllVendors, vendors])

  const vendorCounts = useMemo(() => {
    const map = new Map()
    vendors.forEach(vendor => {
      map.set(String(vendor._id), countVendorUsers(employees, vendor._id))
    })
    return map
  }, [employees, vendors])

  const vendorInsights = useMemo(() => {
    const map = new Map()
    vendors.forEach(vendor => {
      const userCounts = vendorCounts.get(String(vendor._id)) || countVendorUsers(employees, vendor._id)
      const crewCount = countVendorCrews(crews, vendor._id)
      map.set(String(vendor._id), {
        userCounts,
        ...getVendorLaunchState(vendor, userCounts, crewCount)
      })
    })
    return map
  }, [crews, employees, vendorCounts, vendors])

  const filteredVendors = useMemo(() => {
    return [...visibleVendors].sort((a, b) => {
      const aInsight = vendorInsights.get(String(a._id))
      const bInsight = vendorInsights.get(String(b._id))
      const priorityDifference = getVendorSortPriority(aInsight?.stageKey) - getVendorSortPriority(bInsight?.stageKey)
      if (priorityDifference !== 0) return priorityDifference
      return (a.name || '').localeCompare(b.name || '')
    })
  }, [vendorInsights, visibleVendors])

  const handleVendorListKeyDown = useCallback((e) => {
    if (!filteredVendors?.length) return
    const currentIndex = filteredVendors.findIndex(v => String(v._id) === String(selectedVendorId || ''))

    let nextIndex = currentIndex
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      nextIndex = Math.min(currentIndex + 1, filteredVendors.length - 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      nextIndex = Math.max(currentIndex - 1, 0)
    } else {
      return
    }

    if (nextIndex !== currentIndex) {
      const nextVendor = filteredVendors[nextIndex]
      setSelectedVendorId(nextVendor._id)
      const el = vendorItemRefs.current[String(nextVendor._id)]
      if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [filteredVendors, selectedVendorId])

  useEffect(() => {
    if (!filteredVendors.length) {
      setSelectedVendorId(null)
      return
    }

    const stillVisible = filteredVendors.some(item => String(item._id) === String(selectedVendorId || ''))
    if (!stillVisible) {
      setSelectedVendorId(filteredVendors[0]._id)
    }
  }, [filteredVendors, selectedVendorId])

  const selectedVendor = useMemo(
    () => filteredVendors.find(item => String(item._id) === String(selectedVendorId || '')) || null,
    [filteredVendors, selectedVendorId]
  )

  const selectedInsight = useMemo(() => {
    if (!selectedVendor?._id) return null
    return vendorInsights.get(String(selectedVendor._id)) || null
  }, [selectedVendor, vendorInsights])

  const selectedVendorEmployees = useMemo(() => {
    if (!selectedVendor?._id) return []
    return employees.filter((employee) => String(employee.associatedSub || '') === String(selectedVendor._id))
  }, [employees, selectedVendor])

  const selectedVendorCrews = useMemo(() => {
    if (!selectedVendor?._id) return []
    return crews.filter((crew) => String(crew.subcontractorVendor || '') === String(selectedVendor._id))
  }, [crews, selectedVendor])

  const pageSummary = useMemo(() => {
    return filteredVendors.reduce((summary, vendor) => {
      const insight = vendorInsights.get(String(vendor._id))
      if (!insight) return summary

      summary.visibleVendors += 1
      summary.ready += insight.stageKey === 'ready' ? 1 : 0
      summary.setup += insight.stageKey === 'setup' ? 1 : 0
      summary.pendingInvites += insight.userCounts.invited
      summary.portalUsers += insight.userCounts.portalEnabled
      summary.crews += insight.crewCount
      return summary
    }, {
      visibleVendors: 0,
      ready: 0,
      setup: 0,
      pendingInvites: 0,
      portalUsers: 0,
      crews: 0
    })
  }, [filteredVendors, vendorInsights])

  const handleVendorUpdated = (updatedVendor) => {
    setVendors((current) => current.map(item => (item._id === updatedVendor._id ? updatedVendor : item)))
    setEditingVendor(null)
    setSelectedVendorId(updatedVendor._id)
    loadData()
  }

  const handleVendorCreated = (newVendor) => {
    setVendors((current) => [newVendor, ...current.filter(item => item._id !== newVendor._id)])
    setShowCreateVendorModal(false)
    setShowAllVendors(true)
    setSelectedVendorId(newVendor._id)
    setEditingVendor(newVendor)
  }

  // ─── Loading state ──────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={28} style={{ color: TEAL }} />
      </div>
    )
  }

  // ─── Selected vendor detail counts ──────────────────────────────

  const selCounts = selectedVendor ? vendorCounts.get(String(selectedVendor._id)) || { total: 0, active: 0, invited: 0, admins: 0 } : null

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <div className={classes.page}>

      {/* ── Compact header bar ─────────────────────────────────── */}
      <div className={classes.headerBar}>
        <div className={classes.headerLeft}>
          <span className={classes.headerTitle}>Subcontractor Admin</span>
          <div className={classes.headerDivider} />
          <div className={classes.headerMetrics}>
            <div className={classes.headerMetric}>
              <span className={classes.headerMetricValue}>{pageSummary.visibleVendors}</span>
              <span className={classes.headerMetricLabel}>vendors</span>
            </div>
            <div className={classes.headerMetric}>
              <span className={classes.headerMetricValue} style={{ color: STATUS_COLORS.setup.dot }}>{pageSummary.setup}</span>
              <span className={classes.headerMetricLabel}>need setup</span>
            </div>
            <div className={classes.headerMetric}>
              <span className={classes.headerMetricValue} style={{ color: STATUS_COLORS.ready.dot }}>{pageSummary.ready}</span>
              <span className={classes.headerMetricLabel}>ready</span>
            </div>
            <div className={classes.headerMetric}>
              <span className={classes.headerMetricValue} style={{ color: TEAL }}>{pageSummary.pendingInvites}</span>
              <span className={classes.headerMetricLabel}>pending invites</span>
            </div>
            <div className={classes.headerMetric}>
              <span className={classes.headerMetricValue}>{pageSummary.portalUsers}</span>
              <span className={classes.headerMetricLabel}>portal users</span>
            </div>
            <div className={classes.headerMetric}>
              <span className={classes.headerMetricValue}>{pageSummary.crews}</span>
              <span className={classes.headerMetricLabel}>crews</span>
            </div>
          </div>
        </div>
        <div className={classes.headerActions}>
          <Tooltip title="Refresh data">
            <IconButton size="small" onClick={loadData} style={{ color: TEXT_SECONDARY }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowCreateVendorModal(true)}
            style={{
              borderRadius: 9,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderColor: TEAL,
              color: TEAL_DARK
            }}
          >
            + New Vendor
          </Button>
        </div>
      </div>

      {/* ── Main two-column layout ─────────────────────────────── */}
      <div className={classes.mainContent}>

        {/* ── Vendor sidebar ─────────────────────────────────── */}
        <div className={classes.sidebar}>
          <div className={classes.sidebarHeader}>
            <div className={classes.sidebarTitleRow}>
              <span className={classes.sidebarTitle}>Vendors</span>
              <span className={classes.vendorCount}>{filteredVendors.length}</span>
            </div>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search by name, phone, address..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className={classes.searchField}
            />
            <div className={classes.filterRow}>
              <FormControlLabel
                control={(
                  <Switch
                    size="small"
                    color="primary"
                    checked={showAllVendors}
                    onChange={(e) => setShowAllVendors(e.target.checked)}
                    className={classes.filterSwitch}
                  />
                )}
                label={
                  <span className={classes.filterLabel}>
                    {showAllVendors ? `All vendors (${vendors.length})` : `Subcontractors only (${subcontractorCount})`}
                  </span>
                }
              />
            </div>
          </div>

          <div className={classes.vendorList} ref={vendorListRef} tabIndex={0} onKeyDown={handleVendorListKeyDown}>
            {filteredVendors.map((vendor) => {
              const counts = vendorCounts.get(String(vendor._id)) || { total: 0, active: 0, invited: 0, admins: 0 }
              const insight = vendorInsights.get(String(vendor._id)) || getVendorLaunchState(vendor, counts, 0)
              const isSelected = String(selectedVendorId || '') === String(vendor._id)
              const statusColor = STATUS_COLORS[insight.stageKey] || STATUS_COLORS.setup

              return (
                <div
                  key={vendor._id}
                  ref={el => { vendorItemRefs.current[String(vendor._id)] = el }}
                  onClick={() => setSelectedVendorId(vendor._id)}
                  className={`${classes.vendorItem} ${isSelected ? classes.vendorItemSelected : ''}`}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <span className={classes.vendorName}>{vendor.name}</span>
                    <span
                      className={classes.statusBadge}
                      style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                    >
                      {insight.stageLabel}
                    </span>
                  </Box>
                  <div className={classes.vendorMeta}>
                    <span className={classes.vendorMetaItem}>
                      <FiberManualRecordIcon className={classes.statusDot} style={{ color: vendor.isSubcontractor ? STATUS_COLORS.ready.dot : TEXT_MUTED }} />
                      {vendor.isSubcontractor ? 'Sub' : 'Vendor'}
                    </span>
                    <span className={classes.vendorMetaItem}>{counts.total} users</span>
                    <span className={classes.vendorMetaItem}>{counts.admins} admins</span>
                    <span className={classes.vendorMetaItem}>{insight.crewCount} crews</span>
                    {counts.invited > 0 && (
                      <span className={classes.vendorMetaItem} style={{ color: TEAL_DARK, fontWeight: 600 }}>
                        {counts.invited} invited
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
            {!filteredVendors.length && (
              <div className={classes.emptyVendorList}>
                <Typography variant="body2" style={{ fontWeight: 600, color: TEXT_PRIMARY, marginBottom: 4 }}>
                  No vendors match this filter
                </Typography>
                <Typography variant="caption" style={{ color: TEXT_MUTED }}>
                  Try enabling "All vendors" to see non-subcontractor orgs.
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* ── Detail panel ───────────────────────────────────── */}
        <div className={classes.detailPanel}>
          {!selectedVendor ? (
            <div className={classes.emptyState}>
              <Typography className={classes.emptyTitle}>
                Select a vendor
              </Typography>
              <Typography className={classes.emptyDesc}>
                Choose a vendor from the list to manage subcontractor access, portal settings, crew alignment, and invitations.
              </Typography>
            </div>
          ) : (
            <div className={classes.detailScroll}>

              {/* ── Sticky vendor name bar ────────────────────── */}
              <div className={classes.vendorHeaderSticky}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <span className={classes.stickyName}>{selectedVendor.name}</span>
                  <Chip
                    size="small"
                    label={selectedInsight?.stageLabel || 'Needs Review'}
                    className={classes.vendorStatusChip}
                    style={{
                      backgroundColor: (STATUS_COLORS[selectedInsight?.stageKey] || STATUS_COLORS.setup).bg,
                      color: (STATUS_COLORS[selectedInsight?.stageKey] || STATUS_COLORS.setup).text
                    }}
                  />
                </div>
                <div className={classes.stickyRight}>
                  <Chip
                    size="small"
                    label={selectedVendor.isSubcontractor ? 'Sub' : 'Vendor'}
                    className={classes.vendorStatusChip}
                    style={selectedVendor.isSubcontractor
                      ? { backgroundColor: STATUS_COLORS.ready.bg, color: STATUS_COLORS.ready.text }
                      : { backgroundColor: WARM_BG, color: WARM }
                    }
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    className={classes.editButton}
                    onClick={() => setEditingVendor(selectedVendor)}
                  >
                    Edit Settings
                  </Button>
                </div>
              </div>

              {/* ── Vendor info card ──────────────────────────── */}
              <div className={classes.vendorHeader}>
                <div className={classes.vendorHeaderTop}>
                  <div>
                    <div className={classes.vendorDetailName}>{selectedVendor.name}</div>
                    <div className={classes.vendorContact}>
                      {[selectedVendor.address, selectedVendor.phoneNumber].filter(Boolean).join('  ·  ') || 'No contact info on file'}
                    </div>
                  </div>
                </div>

                <div className={classes.vendorStatusRow}>
                  <Chip
                    size="small"
                    label={selectedVendor.isSubcontractor ? 'Subcontractor' : 'Standard Vendor'}
                    className={classes.vendorStatusChip}
                    style={selectedVendor.isSubcontractor
                      ? { backgroundColor: STATUS_COLORS.ready.bg, color: STATUS_COLORS.ready.text }
                      : { backgroundColor: WARM_BG, color: WARM }
                    }
                  />
                  <Chip
                    size="small"
                    label={selectedVendor.mobileAccessEnabled ? 'Mobile On' : 'Mobile Off'}
                    className={classes.vendorStatusChip}
                    style={{ backgroundColor: TEAL_BG, color: TEAL_DARK }}
                  />
                  <Chip
                    size="small"
                    label={`Onboarding: ${selectedVendor.onboardingStatus || 'draft'}`}
                    className={classes.vendorStatusChip}
                    style={{ backgroundColor: SURFACE_TINTED, color: TEXT_SECONDARY, border: `1px solid ${BORDER}` }}
                  />
                </div>

                <div className={classes.vendorMetrics}>
                  <div className={classes.vendorMetricPill}>
                    <span className={classes.vendorMetricPillValue}>{selCounts?.total || 0}</span>
                    <span className={classes.vendorMetricPillLabel}>users</span>
                  </div>
                  <div className={classes.vendorMetricPill}>
                    <span className={classes.vendorMetricPillValue}>{selCounts?.admins || 0}</span>
                    <span className={classes.vendorMetricPillLabel}>admins</span>
                  </div>
                  <div className={classes.vendorMetricPill}>
                    <span className={classes.vendorMetricPillValue}>{selCounts?.invited || 0}</span>
                    <span className={classes.vendorMetricPillLabel}>invited</span>
                  </div>
                  <div className={classes.vendorMetricPill}>
                    <span className={classes.vendorMetricPillValue}>{selectedInsight?.crewCount || 0}</span>
                    <span className={classes.vendorMetricPillLabel}>crews</span>
                  </div>
                </div>

                {selectedInsight?.actionItems?.[0]?.detail && (
                  <div className={classes.vendorHint}>
                    {selectedInsight.actionItems[0].detail}
                  </div>
                )}
              </div>

              {/* ── Checklist + Actions (collapsible) ────────── */}
              <div className={classes.collapsibleSection}>
                <div className={classes.collapsibleHeader} onClick={() => toggleSection('checklist')}>
                  <div className={classes.collapsibleHeaderLeft}>
                    <span className={classes.collapsibleTitle}>Launch Checklist & Next Actions</span>
                    {selectedInsight?.checklist && (
                      <Chip
                        size="small"
                        label={`${selectedInsight.checklist.filter(c => c.complete).length}/${selectedInsight.checklist.length}`}
                        className={classes.vendorStatusChip}
                        style={{ backgroundColor: TEAL_BG, color: TEAL_DARK }}
                      />
                    )}
                  </div>
                  <ExpandMoreIcon className={`${classes.collapsibleChevron} ${!collapsedSections.checklist ? classes.collapsibleChevronOpen : ''}`} />
                </div>
                <div className={`${classes.collapsibleBody} ${!collapsedSections.checklist ? classes.collapsibleBodyOpen : classes.collapsibleBodyClosed}`}>
                  <div className={classes.collapsibleContent}>
                    <div className={classes.twoColGrid} style={{ marginBottom: 0 }}>
                      <div>
                        <div className={classes.cardLabel}>Checklist</div>
                        {(selectedInsight?.checklist || []).map((item) => (
                          <div key={item.label} className={classes.checklistRow}>
                            {item.complete
                              ? <CheckCircleIcon className={`${classes.checkIcon} ${classes.checkIconDone}`} />
                              : <RadioButtonUncheckedIcon className={`${classes.checkIcon} ${classes.checkIconPending}`} />
                            }
                            <div>
                              <div className={classes.checklistText}>{item.label}</div>
                              <div className={classes.checklistDetail}>{item.detail}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <div className={classes.cardLabel}>Next Actions</div>
                        {(selectedInsight?.actionItems || []).map((item) => (
                          <div key={item.title} className={classes.actionRow}>
                            <ArrowForwardIcon className={classes.actionArrow} />
                            <div>
                              <div className={classes.actionTitle}>{item.title}</div>
                              <div className={classes.actionDetail}>{item.detail}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Messaging panel (collapsible) ────────────── */}
              <div className={classes.collapsibleSection}>
                <div className={classes.collapsibleHeader} onClick={() => toggleSection('messaging')}>
                  <div className={classes.collapsibleHeaderLeft}>
                    <span className={classes.collapsibleTitle}>Messaging</span>
                  </div>
                  <ExpandMoreIcon className={`${classes.collapsibleChevron} ${!collapsedSections.messaging ? classes.collapsibleChevronOpen : ''}`} />
                </div>
                <div className={`${classes.collapsibleBody} ${!collapsedSections.messaging ? classes.collapsibleBodyOpen : classes.collapsibleBodyClosed}`}>
                  <div className={classes.collapsibleContent}>
                    <SubcontractorMessagingPanel
                      vendor={selectedVendor}
                      employees={selectedVendorEmployees}
                      crews={selectedVendorCrews}
                      notification={notification}
                    />
                  </div>
                </div>
              </div>

              {/* ── Management panel (collapsible) ───────────── */}
              <div className={classes.collapsibleSection}>
                <div className={classes.collapsibleHeader} onClick={() => toggleSection('management')}>
                  <div className={classes.collapsibleHeaderLeft}>
                    <span className={classes.collapsibleTitle}>Vendor Management</span>
                  </div>
                  <ExpandMoreIcon className={`${classes.collapsibleChevron} ${!collapsedSections.management ? classes.collapsibleChevronOpen : ''}`} />
                </div>
                <div className={`${classes.collapsibleBody} ${!collapsedSections.management ? classes.collapsibleBodyOpen : classes.collapsibleBodyClosed}`}>
                  <div className={classes.collapsibleContent}>
                    <SubcontractorManagementPanel vendor={selectedVendor} onEditVendor={() => setEditingVendor(selectedVendor)} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────── */}
      <EditVendorDetailsModal
        open={!!editingVendor}
        vendor={editingVendor || {}}
        vendors={vendors}
        closeModal={() => setEditingVendor(null)}
        updateParent={handleVendorUpdated}
      />
      <NewSupplierModal
        open={showCreateVendorModal}
        name=""
        closeModal={() => setShowCreateVendorModal(false)}
        updateParent={handleVendorCreated}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorAdminPage)
